export const formatTimestamp = (postgresTimestamp: Date) => {
  const date = new Date(postgresTimestamp);
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: 'numeric',
    hour12: true,
  });
};

export const formatDate = (dateString: string | number | Date | null | undefined) => {
  if (!dateString || !(new Date(dateString) instanceof Date) || isNaN(new Date(dateString).getTime())) {
    return null;
  }
  const dateObj = new Date(dateString);
  const options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: 'numeric',
    hour12: true,
  };
  return dateObj.toLocaleString('en-US', options).replace(/,/g, '');
};

export const formatDateMDY = (dateString: string | number | Date | null | undefined) => {
  if (!dateString || !(new Date(dateString) instanceof Date) || isNaN(new Date(dateString).getTime())) {
    return null;
  }
  const dateObj = new Date(dateString);
  const options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  };
  return dateObj.toLocaleString('en-US', options);
};

export const isValidEmail = (email: string) => {
  const regex = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,})$/;
  return regex.test(email);
};

export const isValidHttpsUrl = (url: string) => {
  try {
    const parsed = new URL(url);
    const isHttps = parsed.protocol === 'https:';

    // check if the url is a valid https url with regex
    const regex = /^(https:\/\/)(www\.)?[a-zA-Z0-9-]+(\.[a-zA-Z0-9-]+)+([/?].*)?$/;
    const regexRes = regex.test(url);

    return isHttps && regexRes;
  } catch (e) {
    return false;
  }
};

export const capitalizeWords = (str: string) => {
  return str.replace(/(?:^|\s|_|-)\S/g, (c) => c.toUpperCase());
};

export const formatSnakeCase = (str: string) => {
  return str.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
};

export const convertDate = (date: Date) => {
  return new Date(date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
  });
};

export const convertDateTime = (date: Date) => {
  return new Date(date).toLocaleString('en-US', {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
};

export const isDevEnvironment = () => {
  if (typeof window === 'undefined') {
    return false;
  }
  const hostname = window.location?.hostname;
  return hostname === 'localhost' || hostname === 'dev-astur-admin.acenda.net';
};

// takes in set percentage and set message as state
export const configureProgressBar = (
  setPercentage: (percentage: number | undefined) => void,
  setMessage: (message: string | undefined) => void,
  options: {
    percentage: number;
    message: string;
    delay: number;
  }[]
) => {
  options.forEach((option, index) => {
    setTimeout(() => {
      setPercentage(option.percentage);
      setMessage(option.message);
    }, option.delay);
  });

  // setTimeout(
  // 	() => {
  // 		setPercentage(undefined);
  // 		setMessage(undefined);
  // 	},
  // 	options[options.length - 1].delay + 500
  // );
};

export const isValidDateTemplate = (template: string) => {
  // Valid date template patterns like yyyy-mm-dd, MM/dd/yyyy, etc.
  const validPatterns = [/^yyyy-mm-dd$/, /^MM\/dd\/yyyy$/, /^dd\/MM\/yyyy$/, /^yyyy\/MM\/dd$/, /^yyyy\/dd\/MM$/, /^MM-dd-yyyy$/, /^dd-MM-yyyy$/];

  return validPatterns.some((pattern) => pattern.test(template));
};

export const isValidHost = (host: string) => {
  // Valid host patterns:
  // - domain.com
  // - sub.domain.com
  // - IP address (IPv4)
  const domainPattern = /^([a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}$/;
  const ipPattern = /^(\d{1,3}\.){3}\d{1,3}$/;

  return domainPattern.test(host) || ipPattern.test(host);
};

export const isValidPort = (port: number) => {
  // Port must be between 1 and 65535
  return port >= 1 && port <= 65535;
};

export const isValidCronFrequency = (cron: string) => {
  const parts = cron.trim().split(/\s+/);
  if (parts.length !== 5) return false;

  const limits = [
    { min: 0, max: 59 }, // Minute
    { min: 0, max: 23 }, // Hour
    { min: 1, max: 31 }, // Day
    { min: 1, max: 12 }, // Month
    { min: 0, max: 6 }, // Weekday
  ];

  const starOnly = /^\*(\/[1-9][0-9]?)?$/; // * or */step (step 1–99)
  const valueOnly = /^(\d{1,2})(-\d{1,2})?(\/[1-9][0-9]?)?$/; // n, n-n, n/step, or n-n/step

  const checkSegment = (seg: string, min: number, max: number): boolean => {
    if (starOnly.test(seg)) return true;
    const m = seg.match(valueOnly);
    if (!m) return false;
    const start = parseInt(m[1], 10);
    const end = m[2] ? parseInt(m[2].slice(1), 10) : start;
    return !Number.isNaN(start) && !Number.isNaN(end) && start <= end && start >= min && start <= max && end >= min && end <= max;
  };

  return parts.every((part, i) => {
    const segments = part.split(',');
    const { min, max } = limits[i];

    if (segments.length === 1) return checkSegment(segments[0], min, max);

    for (const seg of segments) {
      if (starOnly.test(seg)) return false; // * not allowed in a list
      if (!checkSegment(seg, min, max)) return false;
    }
    return true;
  });
};

const parseCronMinuteValues = (minutePart: string): number[] | null => {
  const values = new Set<number>();

  const addRange = (start: number, end: number, step = 1) => {
    if (step <= 0 || start < 0 || end > 59 || start > end) return false;
    for (let value = start; value <= end; value += step) {
      values.add(value);
    }
    return true;
  };

  for (const segment of minutePart.split(',')) {
    if (segment === '*') {
      addRange(0, 59);
      continue;
    }

    const wildcardStep = segment.match(/^\*\/([1-9][0-9]?)$/);
    if (wildcardStep) {
      addRange(0, 59, Number(wildcardStep[1]));
      continue;
    }

    const valueMatch = segment.match(/^(\d{1,2})(?:-(\d{1,2}))?(?:\/([1-9][0-9]?))?$/);
    if (!valueMatch) return null;

    const start = Number(valueMatch[1]);
    const end = valueMatch[2] ? Number(valueMatch[2]) : 59;
    const step = valueMatch[3] ? Number(valueMatch[3]) : 1;
    const actualEnd = valueMatch[2] ? end : start;

    if (!addRange(start, valueMatch[3] && !valueMatch[2] ? end : actualEnd, step)) return null;
  }

  return Array.from(values).sort((a, b) => a - b);
};

export const isCronFrequencyAtLeastEveryMinutes = (cron: string, minimumMinutes: number) => {
  if (!isValidCronFrequency(cron)) return false;

  const [minutePart] = cron.trim().split(/\s+/);
  const minutes = parseCronMinuteValues(minutePart);
  if (!minutes || minutes.length === 0) return false;
  if (minutes.length === 1) return true;

  for (let index = 0; index < minutes.length; index++) {
    const current = minutes[index];
    const next = minutes[(index + 1) % minutes.length];
    const gap = index === minutes.length - 1 ? next + 60 - current : next - current;
    if (gap < minimumMinutes) return false;
  }

  return true;
};

export const is2xx = (status: number) => {
  return status >= 200 && status < 300;
};

export const is4xx = (status: number) => {
  return status >= 400 && status < 500;
};

export const is5xx = (status: number) => {
  return status >= 500 && status < 600;
};

export const CSVToArray = (CSV_string: string, delimiter: string = ','): string[][] => {
  const pattern = new RegExp('(\\' + delimiter + '|\\r?\\n|\\r|^)' + '(?:"([^"]*(?:""[^"]*)*)"|' + '([^"\\' + delimiter + '\\r\\n]*))', 'gi');

  const rows: string[][] = [[]];
  let matches: RegExpExecArray | null = null;

  while ((matches = pattern.exec(CSV_string))) {
    const matched_delimiter = matches[1];
    if (matched_delimiter.length && matched_delimiter !== delimiter) {
      rows.push([]);
    }
    let matched_value: string;
    if (matches[2]) {
      matched_value = matches[2].replace(new RegExp('""', 'g'), '"');
    } else {
      matched_value = matches[3];
    }
    rows[rows.length - 1].push(matched_value);
  }
  return rows;
};

// export const isSimilarString = (str1: string, str2: string): { isMatch: boolean; score: number } => {
//   // Normalize strings
//   const normalize = (str: string): string => {
//     return str
//       .toLowerCase()
//       .replace(/fields\./g, '') // Remove fields. prefix
//       .replace(/[_-]/g, '') // Remove underscores and dashes
//       .replace(/\s+/g, '') // Remove spaces
//       .replace(/[^a-z0-9]/g, ''); // Remove any other special characters
//   };

//   const s1 = normalize(str1);
//   const s2 = normalize(str2);

//   // Exact match after normalization
//   if (s1 === s2) {
//     return { isMatch: true, score: 1 };
//   }

//   // One string contains the other
//   if (s1.includes(s2) || s2.includes(s1)) {
//     return { isMatch: true, score: 0.9 };
//   }

//   // Split into words (handles camelCase)
//   const words1 = s1.split(/(?=[A-Z])/).map((w) => w.toLowerCase());
//   const words2 = s2.split(/(?=[A-Z])/).map((w) => w.toLowerCase());

//   // Count matching words
//   const matchingWords = words1.filter((word1) => words2.some((word2) => word2.includes(word1) || word1.includes(word2)));

//   // Calculate score based on word overlap
//   const score = matchingWords.length / Math.max(words1.length, words2.length);

//   // Consider it a match if score is above threshold
//   return {
//     isMatch: score > 0.8,
//     score,
//   };
// };

export const isSimilarString = (str1: string, str2: string): { isMatch: boolean; score: number } => {
  // Normalize strings
  const normalize = (str: string): string =>
    str
      .toLowerCase()
      .replace(/fields\./g, '') // Remove fields. prefix
      .replace(/[_-]/g, '') // Remove underscores and dashes
      .replace(/\s+/g, '') // Remove spaces
      .replace(/[^a-z0-9]/g, ''); // Remove any other special characters

  const s1 = normalize(str1);
  const s2 = normalize(str2);

  // Exact match after normalization
  if (s1 === s2) {
    return { isMatch: true, score: 1 };
  }

  // One string contains the other
  if (s1.length > 3 && s2.length > 3 && (s1.includes(s2) || s2.includes(s1))) {
    return { isMatch: true, score: 0.9 };
  }

  // Split into "words" (handles camelCase), then filter short tokens
  const words1 = s1
    .split(/(?=[A-Z])/)
    .map((w) => w.toLowerCase())
    .filter((w) => w.length >= 3);

  const words2 = s2
    .split(/(?=[A-Z])/)
    .map((w) => w.toLowerCase())
    .filter((w) => w.length >= 3);

  // Count matching words
  const matchingWords = words1.filter((w1) => words2.some((w2) => w2.includes(w1) || w1.includes(w2)));

  // Calculate score based on word overlap
  const score = matchingWords.length / Math.max(words1.length, words2.length);

  return {
    isMatch: score > 0.8,
    score,
  };
};

// Routes that are not organization-scoped and should not return an organization
const NON_ORG_ROUTE_PREFIXES = ['connect', 'account', 'login', 'logout', 'auth', 'callback'];

export const getCurrentOrganization = () => {
  if (typeof window === 'undefined') return '';

  const pathname = window.location.pathname;
  const pathParts = pathname.split('/').filter((part) => part !== '');

  // Return empty string if no path segments
  if (pathParts.length === 0) return '';

  const firstSegment = pathParts[0];

  // Return empty string for non-org routes to avoid injecting invalid organization headers
  if (NON_ORG_ROUTE_PREFIXES.includes(firstSegment)) {
    return '';
  }

  return firstSegment;
};

export const setLastVisitedOrganization = (organization: string) => {
  if (typeof window === 'undefined' || organization === '' || organization === 'account') return;

  try {
    sessionStorage.setItem('lastVisitedOrganization', organization);
    localStorage.setItem('globalLastVisitedOrganization', organization);
  } catch (error) {
    console.error('Failed to save last visited organization to storage:', error);
  }
};

export const getLastVisitedOrganization = (): string | null => {
  if (typeof window === 'undefined') return null;

  try {
    const sessionOrg = sessionStorage.getItem('lastVisitedOrganization');
    if (sessionOrg) {
      return sessionOrg;
    }

    return localStorage.getItem('globalLastVisitedOrganization');
  } catch (error) {
    console.error('Failed to retrieve last visited organization from storage:', error);
    return null;
  }
};

/**
 * Safe stringify for console/error args (handles circular refs, DOM nodes, etc.).
 * Use when passing arbitrary values to Bugsnag or logging to avoid JSON.stringify throwing.
 */
export const stringifyConsoleArg = (arg: unknown): string => {
  if (arg === null) return 'null';
  if (arg === undefined) return 'undefined';
  if (typeof arg === 'string') return arg;
  if (typeof arg === 'number' || typeof arg === 'boolean') return String(arg);
  if (arg instanceof Error) return arg.message + (arg.stack ? `\n${arg.stack}` : '');
  if (typeof arg === 'object') {
    try {
      // Track only the current traversal path so shared references are preserved,
      // while real cycles are replaced with "[Circular]".
      const ancestors: unknown[] = [];
      const replacer = function (this: unknown, _key: string, value: unknown) {
        if (typeof value === 'object' && value !== null) {
          while (ancestors.length > 0 && ancestors[ancestors.length - 1] !== this) {
            ancestors.pop();
          }
          if (ancestors.includes(value)) return '[Circular]';
          ancestors.push(value);
        }
        return value;
      };
      return JSON.stringify(arg, replacer, 2);
    } catch {
      return String(arg);
    }
  }
  return String(arg);
};
